import { api, setAccessToken } from '@/lib/api';
import type { LoginResponse, User } from '@/types/api';
import { create } from 'zustand';

interface AuthState {
  user: User | null;
  /** true mientras se intenta recuperar la sesion al cargar la app. */
  initializing: boolean;
  /** Mensaje del servidor cuando la sesion se cierra sola (otro dispositivo). */
  endedReason: string | null;

  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<User>;
  setUser: (user: User) => void;
  /** Limpia el estado sin llamar a la API: la sesion ya no existe. */
  clear: (reason?: string) => void;
  dismissEndedReason: () => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  initializing: true,
  endedReason: null,

  async login(email, password) {
    const response = await api.post<LoginResponse>('/auth/login', { email, password });
    setAccessToken(response.accessToken);
    set({ user: response.user, endedReason: null });
    return response.user;
  },

  async logout() {
    try {
      await api.post<void>('/auth/logout');
    } finally {
      setAccessToken(null);
      set({ user: null, endedReason: null });
    }
  },

  /**
   * Se llama una vez al arrancar. La cookie httpOnly del refresh token permite
   * recuperar la sesion tras recargar la pagina sin volver a pedir la clave.
   */
  async restore() {
    try {
      const recovered = await api.restoreSession();
      if (!recovered) {
        set({ user: null, initializing: false });
        return;
      }
      const user = await api.get<User>('/auth/me');
      set({ user, initializing: false });
    } catch {
      setAccessToken(null);
      set({ user: null, initializing: false });
    }
  },

  async changePassword(currentPassword, newPassword) {
    const user = await api.post<User>('/auth/change-password', { currentPassword, newPassword });
    set({ user });
    return user;
  },

  setUser(user) {
    set({ user });
  },

  clear(reason) {
    setAccessToken(null);
    set({ user: null, endedReason: reason ?? null });
  },

  dismissEndedReason() {
    set({ endedReason: null });
  },
}));

/** Atajos de lectura para no repetir comprobaciones por toda la interfaz. */
export const useIsAdmin = () => useAuth((state) => state.user?.role === 'ADMIN');
export const useMustChangePassword = () =>
  useAuth((state) => state.user?.mustChangePassword ?? false);
